import {
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { brandPalette } from "../../../constants/brandPalette";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { RiSearch2Fill } from "react-icons/ri";
import ReactSelect from "react-select";
import { useDispatch, useSelector } from "react-redux";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { adminSelectStyles } from "../../../components/Models/admin-modal-theme";
import {
  examAttendaceRequest,
  notifyParentForExamAbsenceRequest,
} from "../../../modules/admin-modules/reports/Actions";
import { getAllExamsRequest } from "../../../modules/admin-modules/exams/Actions";

const focusRing = "0 0 0 1px rgba(91, 13, 22, 0.22)";
const PAGE_SIZE = 25;

const selectBase = adminSelectStyles({ menuZIndex: 1600 });

const selectStyles = {
  ...selectBase,
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  control: (styles, state) => ({
    ...selectBase.control(styles, state),
    borderRadius: "14px",
    minHeight: "44px",
    minWidth: "260px",
  }),
};

const csvHeaders = [
  { label: "Student name", key: "name" },
  { label: "Phone", key: "phone" },
  { label: "Parent phone", key: "parent_phone" },
];

const ExamAttendanceReport = () => {
  const dispatch = useDispatch();
  const toast = useToast();

  const [examId, setExamId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [message, setMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const exams = useSelector((state) => state.exams.exams);
  const examAttendance = useSelector((state) => state.reports.examAttendance);
  const isLoading = useSelector((state) => state.reports.isLoading);

  useEffect(() => {
    dispatch(getAllExamsRequest());
  }, [dispatch]);

  useEffect(() => {
    setSelectedStudents([]);
    setMessage("");
    setSearchTerm("");
    setCurrentPage(1);
    if (!examId) return;
    dispatch(examAttendaceRequest(examId));
  }, [examId, dispatch]);

  const examOptions = useMemo(() => {
    const raw = Array.isArray(exams) ? exams : [];
    return raw.map((exam) => ({
      value: String(exam.id),
      label: exam.name || `Exam #${exam.id}`,
    }));
  }, [exams]);

  const students = useMemo(() => {
    const raw = Array.isArray(examAttendance) ? examAttendance : [];
    const term = searchTerm.trim().toLowerCase();
    return raw
      .filter((item) => {
        if (!term) return true;
        const name = item?.name ?? "";
        const phone = item?.phone ?? "";
        const parent = item?.parent_phone ?? "";
        return (
          name.toLowerCase().includes(term) ||
          phone.toLowerCase().includes(term) ||
          parent.toLowerCase().includes(term)
        );
      })
      .map((item, index) => ({
        key: item?.id ?? index,
        id: item?.id,
        index: index + 1,
        name: item?.name ?? "—",
        phone: item?.phone ?? "—",
        parent_phone: item?.parent_phone ?? "—",
      }));
  }, [examAttendance, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(students.length / PAGE_SIZE));

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return students.slice(start, start + PAGE_SIZE);
  }, [students, currentPage]);

  const csvData = useMemo(
    () =>
      students.map((row) => ({
        name: row.name,
        phone: row.phone,
        parent_phone: row.parent_phone,
      })),
    [students]
  );

  const allSelected =
    students.length > 0 && students.every((row) => selectedStudents.includes(row.id));

  const pageSelected =
    pageRows.length > 0 && pageRows.every((row) => selectedStudents.includes(row.id));

  const handleToggleStudent = useCallback((studentId) => {
    setSelectedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  }, []);

  const handleSelectAll = useCallback(
    (checked) => {
      if (checked) {
        setSelectedStudents(students.map((row) => row.id).filter(Boolean));
      } else {
        setSelectedStudents([]);
      }
    },
    [students]
  );

  const handleSelectPage = useCallback(
    (checked) => {
      const pageIds = pageRows.map((row) => row.id).filter(Boolean);
      setSelectedStudents((prev) => {
        if (checked) {
          const next = [...prev];
          pageIds.forEach((id) => {
            if (!next.includes(id)) next.push(id);
          });
          return next;
        }
        return prev.filter((id) => !pageIds.includes(id));
      });
    },
    [pageRows]
  );

  const handleSendNotifications = useCallback(() => {
    if (selectedStudents.length === 0) {
      toast({
        title: "No students selected",
        description: "Select at least one student to notify parents.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    dispatch(
      notifyParentForExamAbsenceRequest({
        students_id: selectedStudents,
        message: message.trim() || undefined,
      })
    );
    setSelectedStudents([]);
    setMessage("");
  }, [selectedStudents, message, dispatch, toast]);

  return (
    <Layout>
      <Box
        minH="400px"
        maxW="1440px"
        mx="auto"
        w="100%"
        px={{ base: 4, sm: 5, md: 8, lg: 10 }}
        pt={{ base: 6, md: 8 }}
        pb={{ base: 10, md: 12 }}
      >
        <Flex
          w="100%"
          mb={6}
          justifyContent="space-between"
          alignItems={{ base: "stretch", md: "flex-start" }}
          gap={4}
          flexWrap="wrap"
        >
          <Box flex="1" minW="0">
            <Text fontSize={{ base: "18px", md: "24px" }} fontWeight="900" color="var(--text-primary)">
              Exam absence report
            </Text>
            <Text mt={1} fontSize="sm" fontWeight="700" color="var(--text-muted)">
              Students who did not attend the selected exam. Export the list or notify parents.
            </Text>
          </Box>
          <Flex
            align="flex-start"
            gap={3}
            flexWrap="wrap"
            justifyContent={{ base: "flex-start", md: "flex-end" }}
          >
            <Box minW={{ base: "100%", md: "280px" }} maxW={{ base: "100%", md: "420px" }}>
              <ReactSelect
                options={examOptions}
                placeholder="Select exam"
                styles={selectStyles}
                value={examOptions.find((o) => o.value === String(examId)) || null}
                onChange={(opt) => setExamId(opt?.value ? String(opt.value) : "")}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                isClearable
              />
            </Box>
            <LogoutMenu />
          </Flex>
        </Flex>

        <Flex gap={4} mb={6} flexWrap="wrap" align="flex-end" justify="space-between">
          <Box flex="1" minW={{ base: "100%", md: "320px" }} maxW="420px">
            <Text
              fontSize="xs"
              fontWeight="800"
              color="var(--text-muted)"
              mb={2}
              textTransform="uppercase"
            >
              Search
            </Text>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <RiSearch2Fill color="var(--text-muted)" />
              </InputLeftElement>
              <Input
                placeholder="Name or phone…"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                borderRadius="14px"
                borderColor="var(--border-subtle)"
                bg="var(--surface-muted)"
                color="var(--text-primary)"
                fontWeight="600"
                isDisabled={!examId}
                _focus={{ borderColor: brandPalette.primary, boxShadow: focusRing }}
              />
            </InputGroup>
          </Box>

          {examId && students.length > 0 ? (
            <CSVLink
              data={csvData}
              headers={csvHeaders}
              filename={`exam-absence-${examId}-${dayjs().format("YYYY-MM-DD")}.csv`}
            >
              <Button
                size="sm"
                borderRadius="lg"
                bg={brandPalette.secondary}
                color="white"
                fontWeight="800"
                px={6}
                _hover={{ bg: brandPalette.primary }}
              >
                Export CSV
              </Button>
            </CSVLink>
          ) : null}
        </Flex>

        {examId && students.length > 0 ? (
          <Box
            mb={4}
            p={{ base: 4, md: 5 }}
            border="1px solid"
            borderColor="var(--border-subtle)"
            bg="var(--surface-muted)"
            borderRadius="16px"
          >
            <Flex
              justify="space-between"
              align={{ base: "stretch", md: "center" }}
              gap={3}
              flexWrap="wrap"
            >
              <Text fontSize="sm" fontWeight="700" color="var(--text-primary)">
                {students.length} absent student{students.length === 1 ? "" : "s"}
                {selectedStudents.length > 0
                  ? ` · ${selectedStudents.length} selected`
                  : ""}
              </Text>
              <Checkbox
                isChecked={allSelected}
                isIndeterminate={selectedStudents.length > 0 && !allSelected}
                onChange={(e) => handleSelectAll(e.target.checked)}
                colorScheme="red"
                fontWeight="700"
                color="var(--text-primary)"
              >
                Select all
              </Checkbox>
            </Flex>
          </Box>
        ) : null}

        {selectedStudents.length > 0 ? (
          <Box
            mb={6}
            p={{ base: 4, md: 5 }}
            border="1px solid"
            borderColor="rgba(91, 13, 22, 0.22)"
            bg="rgba(91, 13, 22, 0.06)"
            borderRadius="16px"
          >
            <Text fontSize="sm" fontWeight="800" color="var(--text-primary)" mb={3}>
              Notify parents ({selectedStudents.length} selected)
            </Text>
            <Flex gap={3} align={{ base: "stretch", md: "center" }} flexWrap="wrap">
              <Input
                placeholder="Optional message…"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                flex="1"
                minW={{ base: "100%", md: "280px" }}
                borderRadius="14px"
                borderColor="var(--border-subtle)"
                bg="var(--surface-raised)"
                color="var(--text-primary)"
                fontWeight="600"
                _focus={{ borderColor: brandPalette.primary, boxShadow: focusRing }}
              />
              <Button
                borderRadius="lg"
                bg={brandPalette.primary}
                color="white"
                fontWeight="800"
                px={6}
                _hover={{ bg: brandPalette.secondary }}
                onClick={handleSendNotifications}
                isLoading={isLoading}
                loadingText="Sending…"
              >
                Send notification
              </Button>
            </Flex>
          </Box>
        ) : null}

        <Box
          border="1px solid"
          borderColor="var(--border-subtle)"
          bg="var(--surface-raised)"
          borderRadius="18px"
          p={{ base: 4, md: 6 }}
          boxShadow="0 18px 55px rgba(0,0,0,0.12)"
          overflowX="auto"
          color="var(--text-primary)"
        >
          {!examId ? (
            <Text fontWeight="600" color="var(--text-muted)">
              Select an exam to load absences.
            </Text>
          ) : isLoading && students.length === 0 ? (
            <Text fontWeight="600" color="var(--text-primary)">
              Loading absences…
            </Text>
          ) : students.length === 0 ? (
            <Text fontWeight="600" color="var(--text-muted)">
              No absent students found for this exam.
            </Text>
          ) : (
            <>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th color="var(--text-primary)" w="48px">
                      <Checkbox
                        isChecked={pageSelected}
                        isIndeterminate={
                          pageRows.some((row) => selectedStudents.includes(row.id)) &&
                          !pageSelected
                        }
                        onChange={(e) => handleSelectPage(e.target.checked)}
                        colorScheme="red"
                      />
                    </Th>
                    <Th color="var(--text-primary)">#</Th>
                    <Th color="var(--text-primary)">Student</Th>
                    <Th color="var(--text-primary)">Phone</Th>
                    <Th color="var(--text-primary)">Parent phone</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {pageRows.map((row) => (
                    <Tr key={row.key}>
                      <Td>
                        <Checkbox
                          isChecked={selectedStudents.includes(row.id)}
                          onChange={() => handleToggleStudent(row.id)}
                          colorScheme="red"
                        />
                      </Td>
                      <Td fontWeight="700">#{row.index}</Td>
                      <Td fontWeight="600">{row.name}</Td>
                      <Td>{row.phone}</Td>
                      <Td>{row.parent_phone}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>

              {totalPages > 1 ? (
                <Flex
                  mt={5}
                  justify="space-between"
                  align="center"
                  gap={3}
                  flexWrap="wrap"
                >
                  <Text fontSize="sm" fontWeight="600" color="var(--text-muted)">
                    Showing {(currentPage - 1) * PAGE_SIZE + 1}–
                    {Math.min(currentPage * PAGE_SIZE, students.length)} of {students.length}
                  </Text>
                  <Flex gap={2}>
                    <Button
                      size="sm"
                      borderRadius="lg"
                      variant="outline"
                      borderColor="var(--border-subtle)"
                      isDisabled={currentPage <= 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <Text fontSize="sm" fontWeight="700" alignSelf="center" px={2}>
                      {currentPage} / {totalPages}
                    </Text>
                    <Button
                      size="sm"
                      borderRadius="lg"
                      variant="outline"
                      borderColor="var(--border-subtle)"
                      isDisabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Next
                    </Button>
                  </Flex>
                </Flex>
              ) : null}
            </>
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default ExamAttendanceReport;
